'use client';
import { ReactElement, ReactNode, useState } from 'react';
import classNames from 'classnames';

interface MinimalTabsProps {
  label: string;
  Icon?: () => ReactElement;
}

enum Type {
  CARD = 'card',
  UNDERLINE = 'underline',
}

interface ITabsProps<TabData extends MinimalTabsProps> {
  dataset: TabData[];
  render: (k: TabData) => ReactNode;
  type: 'underline' | 'card';
}

export const Tabs = <TabData extends MinimalTabsProps>({
  dataset,
  render,
  type,
}: ITabsProps<TabData>) => {
  const [currentTab, setCurrentTab] = useState(0);

  return (
    <div className="relative flex grow flex-col overflow-hidden">
      <ul
        className={classNames('z-20 flex shrink-0 overflow-auto', {
          'bg-gray-100': type === Type.CARD,
          'border-b-grey-500 gap-6 border-b': type === Type.UNDERLINE,
        })}
      >
        {dataset.map(({ label, Icon }, index) => (
          <li
            key={label}
            className={classNames(
              type === Type.CARD && [
                'shrink-0 grow border-x border-b transition-colors hover:bg-white/50 md:whitespace-normal',
                currentTab === index
                  ? 'border-transparent bg-white text-default'
                  : 'border-gray-200 text-secondary',
              ],
              type === Type.UNDERLINE && [
                'shrink-0 border-b-2 font-semibold transition-colors hover:text-black',
                currentTab === index
                  ? 'border-accent border-b-2 text-black'
                  : 'border-transparent text-gray-500',
              ]
            )}
          >
            <button
              className={classNames('h-full cursor-pointer text-left', {
                // A card tab is 100px tall in the design, and it gets there
                // slightly top-heavy: a deeper inset above the icon than
                // below the label.
                'w-full min-w-32 px-6 pt-5.5 pb-4': type === Type.CARD,
                'py-3': type !== Type.CARD,
              })}
              onClick={() => setCurrentTab(index)}
            >
              {Icon && (
                <div
                  className={classNames(
                    'text-icon-muted transition-colors',
                    // 14px leaves the design's 21px between the icon's ink
                    // and the label's, once the line box's own leading is
                    // taken off the top.
                    type === Type.CARD ? 'mb-3.5' : 'mb-2',
                    currentTab === index && '!text-primary-default'
                  )}
                >
                  <Icon />
                </div>
              )}
              {label}
            </button>
          </li>
        ))}
      </ul>
      <ul className="grow">
        {dataset.map((tab, index) => (
          <li
            key={tab.label}
            className={classNames('h-full', index !== currentTab && 'hidden')}
          >
            {render(tab)}
          </li>
        ))}
      </ul>
    </div>
  );
};
